// src/features/kommunikation/components/DocumentUploadModal.tsx
// ERWEITERTE VERSION mit automatischem Credential-Lookup

import React, { useState } from 'react';
import { Upload, FileText, CheckCircle2, Loader2, XCircle, User, Lock, AlertTriangle, Info, Calendar } from 'lucide-react';
import { useAuth } from '../../auth';
import { Customer } from '../../../shared/types';
import { ParsedFilename, UploadFile } from '../types/upload.types';
import { customerService } from '../../../services/customer/CustomerService';
import { CustomerSelectionModal } from './CustomerSelectionModal';

// Services (bestehend)
import { documentParserService } from '../services/DocumentParserService';
import { documentStorageService } from '../services/DocumentStorageService';
import { communicationDocumentService } from '../services/CommunicationDocumentService';
import { credentialsService } from '../services/CredentialsService';

interface DocumentUploadModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  // Zeigt nach einem Upload-Versuch die Liste der nicht versendeten Dokumente an.
  // Wird nur gesetzt, wenn nicht alle Dateien erfolgreich hochgeladen wurden.
  const [showReport, setShowReport] = useState(false);
  
  // Globales Versanddatum für alle Dateien (Default: Morgen 2:00 Uhr)
  const getDefaultVersandDatum = (): Date => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(2, 0, 0, 0);
    return tomorrow;
  };
  const [batchVersandDatum, setBatchVersandDatum] = useState<Date>(getDefaultVersandDatum());
  
  /**
   * Formatiert Date für datetime-local Input (lokale Zeit, nicht UTC)
   */
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  
  // NEU: Customer Selection State
  const [customerSelectionModal, setCustomerSelectionModal] = useState<{
    isOpen: boolean;
    fileIndex: number;
    customers: Customer[];
    filename: string;
  } | null>(null);

  /**
   * NEU: Automatischer Lookup von Kunde + Credential
   */
  const lookupCustomerAndCredential = async (
    parsed: ParsedFilename
  ): Promise<{
    customer: Customer | null;
    hasCredential: boolean;
    status: UploadFile['status'];
    error?: string;
  }> => {
    // 1. Kunde(n) finden via Mandantennummer
    const customers = await customerService.findCustomersByMandantennummer(
      parsed.mandantennummer
    );

    if (customers.length === 0) {
      return {
        customer: null,
        hasCredential: false,
        status: 'error',
        error: `Kein Kunde mit Mandantennummer ${parsed.mandantennummer} gefunden`
      };
    }

    // 2. Falls mehrere Kunden → User muss auswählen
    if (customers.length > 1) {
      return {
        customer: null,
        hasCredential: false,
        status: 'needs_customer',
        error: 'Mehrere Kunden gefunden - bitte auswählen'
      };
    }

    // 3. Ein Kunde gefunden → Credential prüfen
    const customer = customers[0];
    
    if (!customer.beraternummer) {
      return {
        customer,
        hasCredential: false,
        status: 'error',
        error: 'Kunde hat keine Beraternummer'
      };
    }

    // 4. Credential-Lookup
    const credential = await credentialsService.findCredential({
      beraternummer: customer.beraternummer,
      mandantennummer: parsed.mandantennummer,
      personalnummer: parsed.personalnummer
    });

    if (!credential) {
      return {
        customer,
        hasCredential: false,
        status: 'no_credential',
        error: `Kein Passwort für Personal-Nr. ${parsed.personalnummer} hinterlegt`
      };
    }

    // Erfolg: Kunde + Credential gefunden
    return {
      customer,
      hasCredential: true,
      status: 'pending',
      error: undefined
    };
  };

  /**
   * Verarbeitet hochgeladene Dateien
   */
  const handleFilesSelected = async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    setParsing(true);
    setShowReport(false);
    
    try {
      const newFiles: UploadFile[] = [];

      // Verarbeite jede Datei
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        // Dateiname parsen
        const parsed = documentParserService.parseFilename(file.name);

        if (!parsed.isValid) {
          newFiles.push({
            file,
            parsed,
            customer: null,
            hasCredential: false,
            passwort: '',
            status: 'error',
            error: parsed.error || 'Ungültiger Dateiname'
          });
          continue;
        }

        // Kunde + Credential lookup
        const lookup = await lookupCustomerAndCredential(parsed);

        // Passwort automatisch setzen falls Credential gefunden
        let passwort = '';
        if (lookup.hasCredential && lookup.customer?.beraternummer) {
          const credential = await credentialsService.findCredential({
            beraternummer: lookup.customer.beraternummer,
            mandantennummer: parsed.mandantennummer,
            personalnummer: parsed.personalnummer
          });
          passwort = credential?.passwort || '';
        }

        newFiles.push({
          file,
          parsed,
          customer: lookup.customer,
          hasCredential: lookup.hasCredential,
          passwort,
          status: lookup.status,
          error: lookup.error
        });
      }

      setFiles(prev => [...prev, ...newFiles]);

      // Falls es Dateien gibt die Customer-Auswahl brauchen, zeige Modal für die erste
      const needsCustomerIndex = newFiles.findIndex(f => f.status === 'needs_customer');
      if (needsCustomerIndex !== -1) {
        const fileWithMultipleCustomers = newFiles[needsCustomerIndex];
        const customers = await customerService.findCustomersByMandantennummer(
          fileWithMultipleCustomers.parsed.mandantennummer
        );
        
        setCustomerSelectionModal({
          isOpen: true,
          fileIndex: files.length + needsCustomerIndex,
          customers,
          filename: fileWithMultipleCustomers.file.name
        });
      }
    } finally {
      setParsing(false);
    }
  };

  /**
   * NEU: Handler für Customer-Auswahl
   */
  const handleCustomerSelected = async (customer: Customer) => {
    if (!customerSelectionModal) return;

    const fileIndex = customerSelectionModal.fileIndex;
    const file = files[fileIndex];

    // Credential-Lookup mit ausgewähltem Kunden
    if (!customer.beraternummer) {
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? {
          ...f,
          customer,
          status: 'error' as const,
          error: 'Kunde hat keine Beraternummer'
        } : f
      ));
      setCustomerSelectionModal(null);
      return;
    }

    const credential = await credentialsService.findCredential({
      beraternummer: customer.beraternummer,
      mandantennummer: file.parsed.mandantennummer,
      personalnummer: file.parsed.personalnummer
    });

    if (!credential) {
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? {
          ...f,
          customer,
          hasCredential: false,
          status: 'no_credential' as const,
          error: `Kein Passwort für Personal-Nr. ${file.parsed.personalnummer} hinterlegt`
        } : f
      ));
    } else {
      // Success
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex ? {
          ...f,
          customer,
          hasCredential: true,
          passwort: credential.passwort,
          status: 'pending' as const,
          error: undefined
        } : f
      ));
    }

    setCustomerSelectionModal(null);

    // Prüfe ob es weitere Dateien gibt die Customer-Auswahl brauchen
    const nextNeedsCustomerIndex = files.findIndex(
      (f, i) => i > fileIndex && f.status === 'needs_customer'
    );
    
    if (nextNeedsCustomerIndex !== -1) {
      const nextFile = files[nextNeedsCustomerIndex];
      const customers = await customerService.findCustomersByMandantennummer(
        nextFile.parsed.mandantennummer
      );
      
      setCustomerSelectionModal({
        isOpen: true,
        fileIndex: nextNeedsCustomerIndex,
        customers,
        filename: nextFile.file.name
      });
    }
  };

  /**
   * Upload-Prozess für eine Datei
   */
  const uploadSingleFile = async (uploadFile: UploadFile): Promise<boolean> => {
    // Validierung
    if (!uploadFile.parsed.isValid) {
      return false;
    }

    if (!uploadFile.hasCredential) {
      return false;
    }

    if (!uploadFile.customer?.beraternummer) {
      return false;
    }

    try {
      // 1. Upload zu Storage
      const storageResult = await documentStorageService.uploadDocument(
        uploadFile.file,
        uploadFile.parsed.mandantennummer,
        uploadFile.parsed.personalnummer,
        uploadFile.parsed.monat,
        uploadFile.parsed.jahr
      );

      if (!storageResult.success || !storageResult.storagePath) {
        throw new Error(storageResult.error || 'Storage-Upload fehlgeschlagen');
      }

      // 2. Speichere in Datenbank (verwende globales batchVersandDatum)
      const dbResult = await communicationDocumentService.uploadDocumentComplete(
        uploadFile.parsed.mandantennummer,
        uploadFile.parsed.personalnummer,
        uploadFile.parsed.monat,
        uploadFile.parsed.jahr,
        uploadFile.parsed.dokumententyp,
        uploadFile.file.name,
        storageResult.storagePath,
        uploadFile.file.size,
        batchVersandDatum,
        uploadFile.passwort,
        user?.email || 'unknown'
      );

      if (!dbResult.success) {
        throw new Error(dbResult.error || 'Datenbank-Speicherung fehlgeschlagen');
      }

      return true;

    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  /**
   * Startet Upload aller validen Dateien
   */
  const handleUploadAll = async () => {
    setUploading(true);
    setShowReport(false);

    const validFiles = files.filter(f => 
      f.parsed.isValid && 
      f.hasCredential &&
      f.customer &&
      f.status === 'pending'
    );

    // Track upload results explicitly
    const uploadResults: Array<{ success: boolean; fileIndex: number }> = [];

    for (let i = 0; i < validFiles.length; i++) {
      const uploadFile = validFiles[i];
      const fileIndex = files.indexOf(uploadFile);

      try {
        // Update Status: uploading
        setFiles(prev => prev.map((f, idx) => 
          idx === fileIndex ? { ...f, status: 'uploading' as const } : f
        ));

        // Upload
        await uploadSingleFile(uploadFile);

        // Update Status: success
        setFiles(prev => prev.map((f, idx) => 
          idx === fileIndex ? { ...f, status: 'success' as const } : f
        ));

        uploadResults.push({ success: true, fileIndex });

      } catch (error) {
        // Update Status: error
        setFiles(prev => prev.map((f, idx) => 
          idx === fileIndex ? { 
            ...f, 
            status: 'error' as const,
            error: error instanceof Error ? error.message : 'Upload fehlgeschlagen'
          } : f
        ));

        uploadResults.push({ success: false, fileIndex });
      }
    }

    setUploading(false);

    // Vollständiger Erfolg gilt nur, wenn ALLE ausgewählten Dateien erfolgreich
    // hochgeladen wurden: keine Datei wurde vorab übersprungen (validFiles deckt
    // alle files ab) UND jeder Upload-Versuch war erfolgreich. Bewusst auf die
    // stabilen lokalen Werte gestützt, da der files-State hier noch veraltet ist
    // (die setFiles-Updates aus der Schleife greifen erst nach diesem Durchlauf).
    // Nur bei vollständigem Erfolg greift das bisherige Verhalten:
    // Erfolgsmeldung + automatisches Schließen.
    const alleErfolgreich =
      files.length > 0 &&
      validFiles.length === files.length &&
      uploadResults.length > 0 &&
      uploadResults.every(r => r.success);

    if (alleErfolgreich) {
      setUploadSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 2000);
    } else {
      // Teil-Upload oder Fehlschlag: Modal bleibt offen, damit die Liste der
      // nicht versendeten Dokumente in Ruhe gelesen werden kann. Bei mindestens
      // einem erfolgreichen Upload wird das Dashboard im Hintergrund aktualisiert
      // (das Modal bleibt dabei geöffnet).
      const mindestensEinErfolg = uploadResults.some(r => r.success);
      if (mindestensEinErfolg) {
        onSuccess();
      }
      setShowReport(true);
    }
  };

  /**
   * Schließt Modal und setzt State zurück
   */
  const handleClose = () => {
    setFiles([]);
    setCustomerSelectionModal(null);
    setShowReport(false);
    onClose();
  };

  /**
   * Zählt hochladbare Dateien
   */
  const uploadableCount = files.filter(f => 
    f.parsed.isValid && 
    f.hasCredential && 
    f.customer &&
    f.status === 'pending'
  ).length;

  /**
   * Nicht versendete Dokumente nach einem Upload-Versuch: alle Dateien, die
   * NICHT den Status 'success' haben (also fehlerhaft, ohne Passwort, ohne
   * Kundenzuordnung oder beim Upload fehlgeschlagen).
   */
  const notSentFiles = files.filter(f => f.status !== 'success');

  /**
   * Status-Icon basierend auf Datei-Status
   */
  const getStatusIcon = (file: UploadFile): React.ReactNode => {
    switch (file.status) {
      case 'success': return <CheckCircle2 size={22} color="#059669" />;
      case 'uploading': return <Loader2 size={22} color="#3B82F6" />;
      case 'error': return <XCircle size={22} color="#DC2626" />;
      case 'needs_customer': return <User size={22} color="#F59E0B" />;
      case 'no_credential': return <Lock size={22} color="#DC2626" />;
      case 'pending': 
        return file.hasCredential
          ? <CheckCircle2 size={22} color="#059669" />
          : <AlertTriangle size={22} color="#F59E0B" />;
      default: return <FileText size={22} color="#6B7280" />;
    }
  };

  /**
   * Status-Text für Anzeige
   */
  const getStatusText = (file: UploadFile): string => {
    if (file.error) return file.error;
    
    switch (file.status) {
      case 'success': return 'Erfolgreich hochgeladen';
      case 'uploading': return 'Upload läuft...';
      case 'needs_customer': return 'Kunde auswählen erforderlich';
      case 'no_credential': return 'Kein Passwort hinterlegt';
      case 'pending':
        if (!file.hasCredential) return 'Kein Passwort hinterlegt';
        return 'Bereit zum Upload';
      default: return 'Unbekannter Status';
    }
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '85vh',
            overflow: 'auto',
            border: '1px solid #E5E7EB',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '8px',
              backgroundColor: '#3B82F615', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FileText size={18} color="#3B82F6" />
            </div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
              Dokumente hochladen
            </h2>
          </div>

          {/* Report: Nicht versendete Dokumente (erscheint nach Upload-Versuch,
              wenn nicht alle Dateien erfolgreich waren). Ganz oben platziert,
              damit die Fehler ohne Scrollen sichtbar sind. */}
          {showReport && notSentFiles.length > 0 && (
            <div
              style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '10px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <AlertTriangle size={20} color="#DC2626" style={{ flexShrink: 0 }} />
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#991B1B' }}>
                  Nicht versendete Dokumente ({notSentFiles.length})
                </div>
              </div>
              <div style={{ fontSize: '13px', color: '#991B1B', marginBottom: '12px' }}>
                {notSentFiles.length} von {files.length} Dokumenten wurden nicht versendet und erreichen den Empfänger nicht. Bitte prüfen:
              </div>
              <div
                style={{
                  maxHeight: '220px',
                  overflowY: 'auto',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #FECACA'
                }}
              >
                {notSentFiles.map((file, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '10px 12px',
                      borderBottom: idx < notSentFiles.length - 1 ? '1px solid #FEE2E2' : 'none'
                    }}
                  >
                    <XCircle size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', wordBreak: 'break-all' }}>
                        {file.file.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#991B1B' }}>
                        {getStatusText(file)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', fontSize: '14px' }}>
            <div style={{ fontWeight: '600', marginBottom: '4px', color: '#1E40AF' }}>Dateiname-Format:</div>
            <code style={{ display: 'block', backgroundColor: 'white', padding: '8px', borderRadius: '6px', fontSize: '12px', border: '1px solid #E5E7EB' }}>
              Mandantennummer_Personalnummer_Nachname_Vorname_Monat_Jahr_Dokumententyp.pdf
            </code>
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Info size={14} color="#6B7280" style={{ flexShrink: 0 }} />
              <span>Kunde und Passwort werden automatisch ermittelt</span>
            </div>
          </div>

          {/* File Upload */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                padding: '24px',
                border: '2px dashed #D1D5DB',
                borderRadius: '10px',
                textAlign: 'center',
                cursor: (parsing || uploading || uploadSuccess) ? 'not-allowed' : 'pointer',
                backgroundColor: (parsing || uploading || uploadSuccess) ? '#F3F4F6' : '#F9FAFB',
                opacity: (parsing || uploading || uploadSuccess) ? 0.6 : 1
              }}
            >
              <input
                type="file"
                accept=".pdf"
                multiple
                disabled={parsing || uploading || uploadSuccess}
                onChange={(e) => handleFilesSelected(e.target.files)}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <Upload size={36} color="#9CA3AF" />
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                PDFs auswählen
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                Mehrere Dateien möglich
              </div>
            </label>
          </div>

          {/* Parsing Loading */}
          {parsing && (
            <div style={{ 
              padding: '16px', 
              marginBottom: '16px',
              textAlign: 'center', 
              color: '#6B7280',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <Loader2 size={28} color="#9CA3AF" />
              </div>
              <div style={{ fontWeight: '600' }}>Dateien werden analysiert...</div>
              <div style={{ fontSize: '14px', marginTop: '4px' }}>Bitte warten</div>
            </div>
          )}

          {/* Upload Success Message */}
          {uploadSuccess && (
            <div style={{ 
              padding: '16px', 
              marginBottom: '16px',
              backgroundColor: '#ECFDF5',
              borderRadius: '8px',
              border: '1px solid #6EE7B7'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle2 size={24} color="#059669" />
                <div>
                  <div style={{ fontWeight: '600', color: '#065F46' }}>Upload erfolgreich!</div>
                  <div style={{ fontSize: '14px', color: '#047857' }}>Dokumente wurden hochgeladen</div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && !uploadSuccess && (
            <div style={{ 
              padding: '16px', 
              marginBottom: '16px',
              backgroundColor: '#EFF6FF',
              borderRadius: '8px',
              border: '1px solid #93C5FD'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Upload size={22} color="#3B82F6" />
                <div>
                  <div style={{ fontWeight: '600', color: '#1E40AF' }}>Upload läuft...</div>
                  <div style={{ fontSize: '14px', color: '#3B82F6' }}>Bitte warten, Dokumente werden hochgeladen</div>
                </div>
              </div>
            </div>
          )}

          {/* Datei-Liste */}
          {files.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                Hochgeladene Dateien ({files.length})
              </h3>

              {files.map((uploadFile, index) => (
                <div
                  key={index}
                  style={{
                    padding: '16px',
                    marginBottom: '8px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    backgroundColor: uploadFile.status === 'error' || uploadFile.status === 'no_credential' || uploadFile.status === 'needs_customer'
                      ? '#FEF2F2'
                      : uploadFile.status === 'success'
                      ? '#F0FDF4'
                      : 'white'
                  }}
                >
                  {/* Dateiname + Status */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      {getStatusIcon(uploadFile)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                        {uploadFile.file.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {getStatusText(uploadFile)}
                      </div>
                    </div>
                  </div>

                  {/* Parsed Info */}
                  {uploadFile.parsed.isValid && (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: '8px',
                      fontSize: '12px',
                      padding: '10px',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '6px',
                      marginBottom: '8px'
                    }}>
                      <div><strong>Mandant:</strong> {uploadFile.parsed.mandantennummer}</div>
                      <div><strong>Personal-Nr:</strong> {uploadFile.parsed.personalnummer}</div>
                      <div><strong>Zeitraum:</strong> {uploadFile.parsed.monat}/{uploadFile.parsed.jahr}</div>
                      <div><strong>Typ:</strong> {uploadFile.parsed.dokumententyp}</div>
                      {uploadFile.customer && (
                        <>
                          <div><strong>Kunde:</strong> {uploadFile.customer.company}</div>
                          <div><strong>Berater-Nr:</strong> {uploadFile.customer.beraternummer}</div>
                        </>
                      )}
                      {uploadFile.hasCredential && (
                        <div style={{ gridColumn: '1 / -1', color: '#059669', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <CheckCircle2 size={14} color="#059669" /> Passwort vorhanden
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Globales Versanddatum */}
          {files.length > 0 && (
            <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>
                <Calendar size={16} color="#6B7280" />
                Versanddatum für alle Dokumente:
              </label>
              <input
                type="datetime-local"
                value={formatDateTimeLocal(batchVersandDatum)}
                onChange={(e) => {
  const [datePart, timePart] = e.target.value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
  setBatchVersandDatum(localDate);
}}
                disabled={parsing || uploading || uploadSuccess}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Standard: Morgen um 2:00 Uhr
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleUploadAll}
              disabled={parsing || uploading || uploadSuccess || uploadableCount === 0}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: (uploadableCount > 0 && !parsing && !uploading && !uploadSuccess) ? '#3B82F6' : '#D1D5DB',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: (uploadableCount > 0 && !parsing && !uploading && !uploadSuccess) ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {parsing ? 'Analysiere...' : uploading ? 'Upload läuft...' : `${uploadableCount} Dokument(e) hochladen`}
            </button>

            <button
              onClick={handleClose}
              disabled={parsing || uploading || uploadSuccess}
              style={{
                padding: '12px 24px',
                backgroundColor: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151'
              }}
            >
              {uploading ? 'Bitte warten...' : 'Schließen'}
            </button>
          </div>
        </div>
      </div>

      {/* Customer Selection Modal */}
      {customerSelectionModal && (
        <CustomerSelectionModal
          isOpen={customerSelectionModal.isOpen}
          customers={customerSelectionModal.customers}
          filename={customerSelectionModal.filename}
          onSelect={handleCustomerSelected}
          onCancel={() => setCustomerSelectionModal(null)}
        />
      )}
    </>
  );
};
